import type { FC } from "react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { Dialog, type DialogProps } from "./Dialog";
import { SquareButton, type SquareButtonKind } from "./buttons/SquareButton";

import { t } from "i18n/i18n";

type ConfirmationDialogProps = Omit<DialogProps, "children" | "onClose"> & {
  cancelButtonText?: string;
  cancelButtonKind?: SquareButtonKind;
  confirmButtonText?: string;
  confirmButtonKind?: SquareButtonKind;
  confirmButtonDisabled?: boolean;
  onConfirm?: () => void | Promise<void>;
  onClose: (confirmed: boolean) => void;
  reverseButtons?: boolean;
  children?: React.ReactNode;
};

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  onClose,
  children,
  cancelButtonText = t("common.cancel"),
  cancelButtonKind = "tertiary",
  confirmButtonText = t("common.confirm"),
  confirmButtonKind = "accent",
  confirmButtonDisabled = false,
  onConfirm = () => {},
  reverseButtons = false,
  ...props
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  return (
    <Dialog {...props} onClose={() => onClose(false)}>
      <>
        {children}

        <div
          className={twMerge(
            "mt-8 flex justify-end gap-2",
            reverseButtons && "flex-row-reverse justify-start",
          )}
        >
          <SquareButton
            eventName="Confirmation modal - Cancel"
            kind={cancelButtonKind}
            size="m"
            title={cancelButtonText}
            onClick={() => onClose(false)}
          >
            {cancelButtonText}
          </SquareButton>

          <SquareButton
            disabled={confirmButtonDisabled || isConfirming}
            eventName="Confirmation modal - Confirm"
            kind={confirmButtonKind}
            size="m"
            title={confirmButtonText}
            onClick={async () => {
              setIsConfirming(true);
              try {
                await onConfirm();
                onClose(true);
              } finally {
                setIsConfirming(false);
              }
            }}
          >
            {confirmButtonText}
          </SquareButton>
        </div>
      </>
    </Dialog>
  );
};
